import { formatMoney, formatMoneyShort, prototypePercent, prototypeSpread, SEASON_DRAWING, seasonSpanToDraw, seasonTiles, type FunnelEstimate, type FunnelGateDto, type SeasonKind, type SeasonStripDto, type SeasonTile, type FunnelPhaseDto, type ProjectFunnelDto } from '@nsb/core';
import { Fragment, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { useWide } from './useWide.ts';
import { endInWords, endLabel, funnelKey, keyWords, type FunnelKeyEntry } from './funnelKey.ts';
import { dateWords } from '../days.ts';
import { axisTicks, perDay, type AxisRoom } from './funnelAxis.ts';
import { funnelFloor, landBand, priceTop } from './funnelFloor.ts';
import { phaseHelp } from './phaseHelp.ts';
import { phaseHeadings } from './phaseHeadings.ts';
import { HowMark } from './ui.tsx';

/**
 * The price funnel (style guide v2 §10, reworked from Jason and Fred's tracker):
 * where the price can still land, narrowing gate by gate over real time from
 * the client's budget; what has been billed; the schedule's first plan
 * and how far it has been pushed out; and the seasons behind it all, painted
 * years of the project's own place laid one per calendar year: its rolling land
 * under the funnel across, its plants beside the funnel down. Exact geometry, drawn here.
 *
 * The master's prototypical job (L-12, core `prototypeFunnel`, `funnel.prototype`) is drawn by the same drawing in its
 * own measures: its time a share of the job's, 0 to 100%, its numbers the price's spread either way (±45%), with no
 * today, money, seasons, schedule or phases' facts, since the master has none of those.
 *
 * A job's phases are named on the band over the drawing, each with a question mark for its facts (#129, `phaseHelp`),
 * and its price scale starts under the lowest figure drawn, the drawing that much shorter (#129, `funnelFloor`).
 */

const C = {
  ink: '#20231f',
  ink2: '#5b6059',
  ink3: '#666961',
  rule: '#d8d3c7',
  ruleStrong: '#aaa69c',
  stone: '#ddd8cd',
  moss: '#586f59',
  slate: '#596e7d',
  cedar: '#b66f48',
  bengara: '#994f42',
  span: '#d6d0c4',
  paper: '#f3f0e8',
};

const DAY = 86_400_000;
/** Words that sit over the painted seasons keep a paper edge, as a map's labels do. */
const HALO = { stroke: C.paper, strokeWidth: 3, strokeLinejoin: 'round', paintOrder: 'stroke' } as const;
/** Each painted year runs past its January a little each side, so neighbours fade into one another at midwinter. */
const SEAM = 0.03;
/** A painted year at about the pixels it will cover on a dense screen; the server sends the nearest width it has. */
const wanted = (units: number, scale: number) => Math.round(units * scale);
const time = (d: string) => Date.parse(`${d}T12:00:00Z`);
// The cost bar's money words: "$412k", "$1.24M" in tight labels (never "$1000k", never "$0k"), whole dollars in sentences.
const k = formatMoneyShort;
const dollars = formatMoney;
const monthYear = (d: string) => dateWords(d.slice(0, 10), 'monthYear');
const fullDay = (d: string) => dateWords(d.slice(0, 10), 'long');
const weeks = (days: number) => (days < 14 ? `${days} days` : days < 60 ? `${Math.round(days / 7)} weeks` : `${Math.round(days / 30)} months`);
/** A gate closing a phase that nobody has dated, placed by the phases' shares of the time (Jason, 2026-10-07): never called dated. */
const PLACED = 'not dated: placed by its phase’s share';
/** The prototypical job's measures (L-12): the price's spread either way, and a day as its share of the job's time. */
const spreadWords = (r: { highCents: number }) => `±${Math.round(prototypeSpread(r) * 100)}%`;
const shareWords = (f: ProjectFunnelDto, on: string) => `${Math.round(prototypePercent(f, on))}%`;
/** Under a mark's name: its share of the time on the master, else when it was done, a planned "~" month, or not dated. */
const markWhen = (f: ProjectFunnelDto, g: FunnelGateDto) => (f.prototype ? shareWords(f, g.on!) : g.state === 'passed' ? monthYear(g.on!) : g.placed ? 'not dated' : `~${monthYear(g.on!)}`);
/** The made-up end's words on a phone, split after "made up:" when they run wider than the drawing (`chars` across). */
const endLines = (madeUp: string, chars: number) => (madeUp.length <= chars ? [madeUp] : [madeUp.slice(0, madeUp.indexOf(':') + 1), madeUp.slice(madeUp.indexOf(':') + 1).trim()]);

/** An element's rendered width, so the labels under the funnel can be laid out in real pixels. */
function useWidth<T extends HTMLElement>(): [React.RefObject<T | null>, number] {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const watch = new ResizeObserver(([e]) => setWidth(Math.round(e!.contentRect.width)));
    watch.observe(el);
    return () => watch.disconnect();
  }, []);
  return [ref, width];
}

/** A label under the funnel across: 6.5rem. */
const LABEL_W = 104;

/**
 * Runs of consecutive band points the budget's cap holds down: what the estimate runs over it by, drawn above the top
 * (design study 17). So far (`opens`), a run opens at the point before its first, where nothing ran over yet, and closes
 * at the first after it that runs over nothing, so the hatch widens and narrows along the band's own straight lines,
 * never a step (P-25: the band runs straight to the budget's day from the estimate before it, and the estimate's top
 * stays where it was set). Ahead, only where two points run over.
 */
function overRuns(pts: ProjectFunnelDto['band'], opens = false): ProjectFunnelDto['band'][] {
  const runs: ProjectFunnelDto['band'][] = [];
  let run: ProjectFunnelDto['band'] = [];
  pts.forEach((p, i) => {
    if (p.overCents) {
      if (!run.length && opens && i > 0) run.push(pts[i - 1]!);
      run.push(p);
    } else if (run.length) {
      if (opens) run.push(p);
      runs.push(run);
      run = [];
    }
  });
  if (run.length) runs.push(run);
  return runs.filter((r) => r.length > 1);
}
/** A band point's top with what runs over it: the estimate's own top. */
const overTop = (p: ProjectFunnelDto['band'][number]) => p.highCents + (p.overCents ?? 0);

/**
 * Scales shared by both layouts: time 0..1 along the project, price 0..1 from zero; the axis laid out for the drawing's
 * own measures (`length` along time, the least room between two ticks' labels: funnelAxis.ts).
 */
function scales(f: ProjectFunnelDto, axis: { length: number; quarters: number; years: number }, floor = 0) {
  const t0 = time(f.startOn);
  const t1 = time(f.endOn);
  const t = (d: string) => Math.min(1, Math.max(0, (time(d) - t0) / (t1 - t0)));
  const max = priceTop(f);
  // From the floor up (#129, `funnelFloor`): what lies under it, spent so far, is drawn at the floor.
  const v = (cents: number) => Math.max(0, (cents - floor) / (max - floor));
  // Quarter-month ticks, Jan, Apr, Jul, Oct, thinned to the years alone where they would crowd (L-21, funnelAxis.ts);
  // on the master's prototypical job, quarters of its time.
  const room: AxisRoom = { perDay: perDay(axis.length, f.startOn, f.endOn), quarters: axis.quarters, years: axis.years };
  const ticks = f.prototype
    ? [0, 25, 50, 75, 100].map((at) => ({ on: new Date(t0 + ((t1 - t0) * at) / 100).toISOString().slice(0, 10), label: `${at}%` }))
    : axisTicks(f.startOn, f.endOn, room);
  // Unclamped, for paintings that run past either end of the chart.
  const u = (d: string) => (time(d) - t0) / (t1 - t0);
  const now = f.endedOn ?? f.today;
  // Spent stays spent: it carries to today, or past the ending to the chart's edge (a closeout bill comes after).
  const lastSpend = f.spent.at(-1)?.on;
  const spentTo = f.endedOn ? f.endOn : lastSpend && lastSpend > now ? lastSpend : now;
  const past = f.band.filter((p) => !p.projected);
  const ahead = f.band.filter((p) => p.projected);
  const todayPoint = past.at(-1);
  return { t, u, v, ticks, now, spentTo, past, future: todayPoint && ahead.length ? [todayPoint, ...ahead] : [] };
}

export function PriceFunnel({ funnel, staff = false }: { funnel: ProjectFunnelDto; staff?: boolean }) {
  const wide = useWide();
  const dated = funnel.gates.filter((g) => g.on);
  // A job's mark opens its detail when it is pressed, never by itself (L-33, Jason, 2026-10-29, of the next mark's open
  // under the funnel: "why is this here?"); pressed again, or Close, it shuts. The master's prototypical job opens its
  // next closer as it always did: that is where it says each closer's share of the time and its spread (L-12).
  const [sel, setSel] = useState(funnel.prototype ? Math.max(0, funnel.gates.findIndex((g) => g.state === 'next')) : -1);
  const pick = (i: number) => setSel((s) => (s === i ? -1 : i));
  if (!funnel.band.length && !dated.length) return null;
  // A job with no gates or milestones (NSB's own, early) has a band and nothing to pick.
  const gate = sel >= 0 ? funnel.gates[sel] : undefined;
  const notReached = funnel.endedOn ? funnel.gates.filter((g) => !g.on).map((g) => g.name) : [];
  return (
    <div>
      {/* Each phase's facts (#129): beside its name on its bar over the funnel, never a second line of names (L-31); a phone,
          with no bars, has the row of their names over the drawing. */}
      {wide ? <Across funnel={funnel} sel={sel} onPick={pick} /> : null}
      {!wide && !funnel.prototype && funnel.phases.length ? <PhaseRow funnel={funnel} /> : null}
      {wide ? null : <Down funnel={funnel} sel={sel} onPick={pick} />}
      <Legend funnel={funnel} />
      {gate ? <GateDetail gate={gate} staff={staff} ended={funnel.endedOn !== null} share={funnel.prototype && gate.on ? shareWords(funnel, gate.on) : null} onClose={() => setSel(-1)} /> : null}
      {notReached.length ? <p className="meta mt-2">Not reached: {notReached.join(', ')}.</p> : null}
    </div>
  );
}

/**
 * Defs shared by both drawings: the hatch for what runs over the budget, the fade that
 * joins one painted year to the next, and the fade that softens the seasons
 * where the chart's time runs out (`from`–`to` along time, `breadth` across it).
 */
function Defs({ id, vertical, from, to, breadth }: { id: string; vertical: boolean; from: number; to: number; breadth: number }) {
  const seam = (2 * SEAM) / (1 + 2 * SEAM);
  const along = (a: number, b: number) => (vertical ? { x1: 0, y1: a, x2: 0, y2: b } : { x1: a, y1: 0, x2: b, y2: 0 });
  return (
    <defs>
      {/* The part of the estimate the budget's cap holds down: over the budget, in bengara (design study 17). */}
      <pattern id={`${id}-over`} width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
        <line x1="0" y1="0" x2="0" y2="6" stroke={C.bengara} strokeOpacity="0.55" strokeWidth="1" />
      </pattern>
      <linearGradient id={`${id}-seam-fade`} {...along(0, 1)}>
        <stop offset="0" stopColor="#fff" stopOpacity="0" />
        <stop offset={seam} stopColor="#fff" stopOpacity="1" />
        <stop offset={1 - seam} stopColor="#fff" stopOpacity="1" />
        <stop offset="1" stopColor="#fff" stopOpacity="0" />
      </linearGradient>
      <mask id={`${id}-seam`} maskContentUnits="objectBoundingBox">
        <rect width="1" height="1" fill={`url(#${id}-seam-fade)`} />
      </mask>
      <linearGradient id={`${id}-ends-fade`} gradientUnits="userSpaceOnUse" {...along(from, to)}>
        <stop offset="0" stopColor="#fff" stopOpacity="0" />
        <stop offset="0.04" stopColor="#fff" stopOpacity="1" />
        <stop offset="0.96" stopColor="#fff" stopOpacity="1" />
        <stop offset="1" stopColor="#fff" stopOpacity="0" />
      </linearGradient>
      <mask id={`${id}-ends`}>
        <rect {...(vertical ? { x: 0, y: from, width: breadth, height: to - from } : { x: from, y: 0, width: to - from, height: breadth })} fill={`url(#${id}-ends-fade)`} />
      </mask>
    </defs>
  );
}

/**
 * The painted seasons under the funnel (core `seasons.ts`; Jason, 2026-10-07: "a 2 month, 6 month, 1, 2, and 3 year
 * version, never clipped on the top, and adjustable so that any time frame can be used and it still looks good"): the
 * strips of the span that suits the funnel's time frame (or the nearest span painted so far), each laid along the dates
 * it covers, a little past each end to fade into its neighbours, at the drawing's full breadth whatever its length, so
 * nothing is cut off. Strips of one slot painted as several (the years before spans) take turns. `place` puts a strip
 * on the drawing.
 */
function Seasons({
  id,
  strips,
  direction,
  from,
  to,
  opacity,
  place,
}: {
  id: string;
  strips: SeasonStripDto[];
  direction: SeasonKind;
  from: string;
  to: string;
  opacity: number;
  place: (tile: SeasonTile) => { x: number; y: number; width: number; height: number; w: number };
}) {
  const span = seasonSpanToDraw(from, to, direction, (slot) => strips.some((s) => s.span === slot.span && s.month === slot.month));
  if (!span) return null;
  return (
    <g opacity={opacity} mask={`url(#${id}-ends)`} aria-hidden="true">
      {seasonTiles(span, from, to).map((tile, i) => {
        const choices = strips.filter((s) => s.span === tile.span && s.month === tile.month).sort((a, b) => a.variant - b.variant);
        const art = choices[i % Math.max(1, choices.length)];
        if (!art) return null;
        const { w, ...at } = place(tile);
        return <image key={tile.from} href={`${art.url}?w=${w}`} {...at} preserveAspectRatio="none" mask={`url(#${id}-seam)`} />;
      })}
    </g>
  );
}

/** Where an estimate's marker sits across: just above the timeline, clear of the marks on it. */
const MARK_Y = 236;

/** The estimates drawn: those set by today (or the day it ended) inside the frame, each with where the band stands on its day. */
function setEstimates(f: ProjectFunnelDto, past: ProjectFunnelDto['band'], now: string) {
  return f.estimates
    .map((e, i) => ({ e, i, at: past.filter((p) => p.on === e.on).at(-1) ?? { lowCents: e.lowCents, highCents: e.highCents } }))
    .filter(({ e }) => e.on >= f.startOn && e.on <= now);
}

/**
 * The corners labelled on the drawing: each estimate set, at its day, with its marker (its index among the estimates);
 * on the master's prototypical job each corner of its band, how sure the price is where each phase closes, with none.
 */
function labelled(f: ProjectFunnelDto, set: ReturnType<typeof setEstimates>, past: ProjectFunnelDto['band']): { key: string; on: string; at: { lowCents: number; highCents: number }; marker: number | null }[] {
  return f.prototype ? past.map((p, i) => ({ key: `corner-${i}`, on: p.on, at: p, marker: null })) : set.map(({ e, i, at }) => ({ key: `est-${i}`, on: e.on, at, marker: i }));
}

const rangeWords = (e: Pick<FunnelEstimate, 'lowCents' | 'highCents'>) => (e.lowCents === e.highCents ? formatMoney(e.lowCents) : `${formatMoney(e.lowCents)} to ${formatMoney(e.highCents)}`);

/**
 * The step an estimate came from, as a circle with its picture at the foot of the estimate's line (Jason, 2026-10-07: "a
 * band has a faint line down to a circle icon that is the node/step that created it, hover over or press to show what it
 * is"): its name, range and day on hover, and pressed, its card under the funnel. A 44px target round it.
 */
function EstimateMarker({ e, selected, onPick, style }: { e: FunnelEstimate; selected: boolean; onPick: () => void; style: React.CSSProperties }) {
  const words = `${e.name}: ${rangeWords(e)}, set ${fullDay(e.on)}`;
  return (
    <button
      type="button"
      title={words}
      aria-label={words}
      aria-pressed={selected}
      onClick={onPick}
      className={`hit absolute flex h-[22px] w-[22px] -translate-x-1/2 -translate-y-1/2 items-center justify-center overflow-hidden rounded-full border bg-paper ${selected ? 'border-ink ring-2 ring-ink/20' : 'border-ink-3 hover:border-ink'}`}
      style={style}
    >
      {e.icon ? <img src={e.icon} alt="" className="h-full w-full object-cover" /> : <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-ink-3" />}
    </button>
  );
}

/** An estimate pressed: the step it came from, its range, its day and the note the client reads. */
function EstimateDetail({ e, onClose }: { e: FunnelEstimate; onClose: () => void }) {
  return (
    <div className="mt-3 flex flex-wrap items-start justify-between gap-x-6 gap-y-1 border-t border-rule pt-3 text-[0.9375rem]">
      <div className="min-w-0 max-w-measure">
        <span className="label block">Estimate</span>
        <span className="small-caps text-ink">{e.name}</span>: <span className="num">{rangeWords(e)}</span>, set {fullDay(e.on)}.{e.note ? ` ${e.note}` : ''}
      </div>
      <button type="button" className="link min-h-11 text-sm text-ink-3 hover:text-ink" onClick={onClose}>
        Close
      </button>
    </div>
  );
}

function Diamond({ state, x, y, size = 13 }: { state: FunnelGateDto['state']; x: number; y: number; size?: number }) {
  const h = size / 2;
  const d = `M${x} ${y - h} L${x + h} ${y} L${x} ${y + h} L${x - h} ${y} Z`;
  const r = size * 0.17;
  if (state === 'passed') return <path d={d} fill={C.moss} />;
  return (
    <g>
      <path d={d} fill={C.paper} stroke={state === 'next' ? C.ink : C.ruleStrong} strokeWidth="1.2" />
      {state === 'next' ? <path d={`M${x} ${y - r} L${x + r} ${y} L${x} ${y + r} L${x - r} ${y} Z`} fill={C.cedar} /> : null}
    </g>
  );
}

/** A gate is a diamond (it locks one step until it is passed); a milestone or an estimate's step is a ring, a point the schedule hangs on. */
function Mark({ g, x, y, size = 13 }: { g: Pick<FunnelGateDto, 'mark' | 'state'>; x: number; y: number; size?: number }) {
  if (g.mark !== 'ring') return <Diamond state={g.state} x={x} y={y} size={size} />;
  const r = size * 0.4;
  if (g.state === 'passed') return <circle cx={x} cy={y} r={r} fill={C.moss} />;
  return (
    <g>
      <circle cx={x} cy={y} r={r} fill={C.paper} stroke={g.state === 'next' ? C.ink : C.ruleStrong} strokeWidth="1.2" />
      {g.state === 'next' ? <circle cx={x} cy={y} r={size * 0.15} fill={C.cedar} /> : null}
    </g>
  );
}

function GateButton({ g, when, selected, onPick, children, style }: { g: FunnelGateDto; when: string; selected: boolean; onPick: () => void; children?: ReactNode; style: React.CSSProperties }) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={selected}
      className={`absolute flex min-h-11 cursor-pointer flex-col justify-center rounded-[2px] px-1.5 text-[0.8125rem] leading-tight transition-colors duration-[120ms] ${selected ? 'bg-inset' : 'hover:bg-inset/60'}`}
      style={style}
    >
      {/* The mark's step named as its tile names it: Title Case (from the API) in small capitals. */}
      <span className="small-caps text-ink">{g.name}</span>
      <span className="num text-[0.6875rem] text-ink-3">{when}</span>
      {children}
    </button>
  );
}

/** Wide screens: time runs across. */
function Across({ funnel: f, sel, onPick }: { funnel: ProjectFunnelDto; sel: number; onPick: (i: number) => void }) {
  const id = useId().replace(/:/g, '');
  const W = 1000;
  // The band as tall as the drawing allows: its foot just above the timeline (Jason, 2026-10-07: "try to keep the funnel maximum height for what is available").
  const [L, R, BASE, AXIS] = [20, 980, 244, 250];
  // Only as tall as the band needs (#129): the price from a floor under its lowest figure, the top of the drawing coming
  // down by what that saves, so a band keeps its height against the price and the empty part under it goes.
  const fl = f.prototype ? { floorCents: 0, share: 1 } : funnelFloor(f);
  const cut = Math.round((BASE - 24) * (1 - fl.share));
  const TOP = 24 + cut;
  // Its axis labels at 10, a DM Mono letter 6 across: "Jan 2010" beside "Oct" wants 40 between them, "2010" beside "2011" 32.
  const s = scales(f, { length: R - L, quarters: 40, years: 32 }, fl.floorCents);
  const [est, setEst] = useState<number | null>(null);
  const [phaseOpen, setPhaseOpen] = useState<string | null>(null);
  const helpId = useId();
  const set = setEstimates(f, s.past, s.now);
  const corners = labelled(f, set, s.past);
  const end = endLabel(f);
  const x = (d: string) => L + s.t(d) * (R - L);
  const y = (c: number) => BASE - s.v(c) * (BASE - TOP);
  const edge = (pts: typeof f.band, key: 'lowCents' | 'highCents') => pts.map((p) => `${x(p.on).toFixed(1)},${y(p[key]).toFixed(1)}`);
  const poly = (pts: typeof f.band) => [...edge(pts, 'highCents'), ...edge(pts, 'lowCents').reverse()].join(' ');
  const line = (pts: typeof f.band, key: 'lowCents' | 'highCents') => `M${edge(pts, key).join(' L')}`;
  // Each corner's figures, its high over the band and its low under it (the master's spread over it alone), each kept
  // clear of the one before: where two would touch, the later a line further from the band.
  const cornerLabels: { key: string; ex: number; tx: number; anchor: 'start' | 'middle' | 'end'; high: string; low: string | null; hy: number; ly: number; marker: number | null; at: { lowCents: number; highCents: number }; spans: [number, number][] }[] = [];
  for (const { key, on, at, marker } of corners) {
    const ex = x(on);
    // Just before today's line, the figures sit to its left, clear of it; on the master, the first and last inside the drawing.
    const nearToday = !f.prototype && x(s.now) - ex >= 0 && x(s.now) - ex < 44;
    const tx = nearToday ? ex - 5 : ex;
    const anchor = nearToday || (f.prototype && ex >= R - 1) ? 'end' : f.prototype && ex <= L + 1 ? 'start' : 'middle';
    const high = f.prototype ? spreadWords(at) : k(at.highCents);
    const low = f.prototype || at.lowCents === at.highCents ? null : k(at.lowCents);
    const span = (text: string): [number, number] => {
      const w = text.length * 6.6;
      return anchor === 'end' ? [tx - w, tx] : anchor === 'start' ? [tx, tx + w] : [tx - w / 2, tx + w / 2];
    };
    const prev = cornerLabels.at(-1);
    const touches = (a: [number, number], b: [number, number], ya: number, yb: number) => a[0] < b[1] + 3 && a[1] > b[0] - 3 && Math.abs(ya - yb) < 13;
    let hy = y(at.highCents) - 6;
    if (prev && touches(span(high), prev.spans[0]!, hy, prev.hy)) hy = Math.min(hy, prev.hy - 13);
    let ly = y(at.lowCents) + 14;
    if (low !== null && prev?.low != null && touches(span(low), prev.spans[1]!, ly, prev.ly)) ly = Math.max(ly, prev.ly + 13);
    cornerLabels.push({ key, ex, tx, anchor, high, low, hy, ly, marker, at, spans: [span(high), span(low ?? '')] });
  }
  const spentPath = f.spent.length
    ? `M${x(f.spent[0]!.on)} ${BASE} ` + f.spent.map((p, i) => `L${x(p.on)} ${i ? y(f.spent[i - 1]!.cumulativeCents) : BASE} L${x(p.on)} ${y(p.cumulativeCents)}`).join(' ') + ` L${x(s.spentTo)} ${y(f.spent.at(-1)!.cumulativeCents)} L${x(s.spentTo)} ${BASE} Z`
    : null;
  // Labels under the axis, in real pixels: each under its mark, nudged a little
  // if that keeps it in a row, else a row further down. Never on top of another.
  const [strip, width] = useWidth<HTMLDivElement>();
  const across = width || 960;
  const taken: { from: number; to: number }[][] = [];
  const placed = f.gates
    .map((g, i) => ({ g, i }))
    .filter(({ g }) => g.on)
    .sort((a, b) => x(a.g.on!) - x(b.g.on!))
    .map(({ g, i }) => {
      const ideal = (x(g.on!) / W) * across - LABEL_W / 2;
      const inside = (l: number) => Math.min(Math.max(l, 0), Math.max(0, across - LABEL_W));
      for (let row = 0; ; row++) {
        const here = (taken[row] ??= []);
        for (const nudge of [0, 10, -10, 20, -20, 28]) {
          const left = inside(ideal + nudge);
          if (here.every((t) => left + LABEL_W + 4 <= t.from || left >= t.to + 4)) {
            here.push({ from: left, to: left + LABEL_W });
            return { g, i, left, row };
          }
        }
      }
    });
  const labelRows = Math.max(1, ...placed.map((p) => p.row + 1));
  const sched = f.schedule;
  const firstGate = f.gates.find((g) => g.on && !g.placed)?.on ?? f.startOn;
  // The phases over the drawing (#129, L-31): each a bar across the dates it spans, its name on it, running past a bar
  // too narrow for it or a line further down (`phaseHeadings`); always in the process's order, a phase not dated yet in
  // its place, dashed (Jason, 2026-10-29: "show the headings for the phases"; L-37). The master's prototypical job has
  // every phase dated, a phase with no share of its time left out.
  const help = !f.prototype;
  const px = (on: string | null) => (on ? (x(on) / W) * across : null);
  const headings = phaseHeadings(
    f.phases
      .map((p) => ({ p, from: px(p.startOn), to: px(p.endOn) }))
      .filter((sp) => !f.prototype || (sp.from !== null && sp.to !== null && sp.to - sp.from > 8)),
    across,
    help,
  );
  const headingRows = Math.max(0, ...headings.map((h) => h.label.row + 1));
  const openHeading = headings.find((h) => h.key === phaseOpen);
  return (
    <div>
      {headings.length ? (
        <div className="relative" style={{ height: 8 + headingRows * 24 }}>
          {headings.map((h) => (
            <Fragment key={h.key}>
              <span
                aria-hidden="true"
                className={`absolute top-0 border-t-2 ${h.dashed ? 'border-dashed' : ''} ${h.phase.state === 'now' ? 'border-cedar' : h.phase.state === 'done' ? 'border-moss/70' : 'border-rule-strong'}`}
                style={{ left: h.bar.from + 2, width: Math.max(0, h.bar.to - h.bar.from - 4) }}
              />
              <span className="absolute flex items-center gap-1.5 whitespace-nowrap" style={{ left: h.label.left, top: 6 + h.label.row * 24 }}>
                <span className={`font-mono text-[0.8125rem] uppercase tracking-[0.06em] ${h.phase.state === 'now' ? 'text-ink' : 'text-ink-3'}`}>{h.name}</span>
                {help ? <HowMark small open={phaseOpen === h.key} onToggle={() => setPhaseOpen(phaseOpen === h.key ? null : h.key)} controls={helpId} label={`What ${h.name} is`} /> : null}
              </span>
            </Fragment>
          ))}
        </div>
      ) : null}
      {openHeading ? <PhaseHelpBox id={helpId} phase={openHeading.phase} funnel={f} style={{ marginLeft: Math.max(0, Math.min(openHeading.label.left, across - 480)) }} /> : null}
      <div className="relative">
      <svg viewBox={`0 ${cut} ${W} ${300 - cut}`} className="block h-auto w-full" role="img" aria-label={ariaSummary(f)}>
        <Defs id={id} vertical={false} from={L} to={R} breadth={AXIS} />
        <clipPath id={`${id}-above-axis`}>
          <rect x="0" y="0" width={W} height={AXIS} />
        </clipPath>
        {/* The land of the place through the seasons, resting on the timeline under the funnel: its paper's empty foot
            let down under the line, so the land meets it (Jason, 2026-10-07: "bring the white space between the bottom
            of the image and the time line lower"). */}
        <g clipPath={`url(#${id}-above-axis)`}>
        <Seasons
          id={id}
          strips={f.seasons.across}
          direction="seasons_across"
          from={f.startOn}
          to={f.endOn}
          opacity={0.42}
          place={(tile) => {
            // Along its dates, the full height under the funnel: never cut off at the top.
            const span = (s.u(tile.to) - s.u(tile.from)) * (R - L);
            const width = span * (1 + 2 * SEAM);
            // From the drawing's top down to the timeline, never cut at the top (art convention), however the floor shortened it.
            const land = landBand(TOP, AXIS);
            return { x: L + s.u(tile.from) * (R - L) - span * SEAM, y: land.y, width, height: land.height, w: wanted(width, 2.4) };
          }}
        />
        </g>
        {f.gates.filter((g) => g.on).map((g) => <path key={g.key} d={`M${x(g.on!)} ${TOP} V${AXIS}`} stroke={C.rule} strokeOpacity={g.mark === 'ring' ? 0.35 : 0.7} />)}
        {spentPath ? <path d={spentPath} fill={C.moss} fillOpacity="0.8" /> : null}
        {s.past.length > 1 ? <polygon points={poly(s.past)} fill={C.stone} fillOpacity="0.92" /> : null}
        {/* What lies ahead, lighter, to its one point at the far end (Jason, 2026-10-07). */}
        {s.future.length > 1 ? <polygon points={poly(s.future)} fill={C.stone} fillOpacity="0.38" /> : null}
        {s.past.length > 1 ? <path d={`${line(s.past, 'highCents')} ${line(s.past, 'lowCents')}`} fill="none" stroke={C.ink} /> : null}
        {s.future.length > 1 ? <path d={`${line(s.future, 'highCents')} ${line(s.future, 'lowCents')}`} fill="none" stroke={C.ink2} strokeDasharray="3 3" /> : null}
        {!corners.length && s.past.length ? (
          <g className="num" fontSize="11" fill={C.ink2}>
            <text x={x(s.now) - 6} y={y(s.past.at(-1)!.highCents) - 6} textAnchor="end">
              {k(s.past.at(-1)!.highCents)}
            </text>
            {s.past.at(-1)!.lowCents !== s.past.at(-1)!.highCents ? (
              <text x={x(s.now) - 6} y={y(s.past.at(-1)!.lowCents) + 14} textAnchor="end">
                {k(s.past.at(-1)!.lowCents)}
              </text>
            ) : null}
            {/* Where it began, only where that clears today's: a job a few weeks old has the two on top of each other. */}
            {s.past[0] && s.past[0].on !== s.now && x(s.now) - x(s.past[0].on) > (k(s.past[0].highCents).length + k(s.past.at(-1)!.highCents).length) * 6.6 + 18 ? (
              <text x={x(s.past[0].on) + 4} y={y(s.past[0].highCents) - 6}>
                {k(s.past[0].highCents)}
              </text>
            ) : null}
          </g>
        ) : null}
        {/* Each estimate at its day (Jason, 2026-10-07: "at that date the high low needs to be listed on the funnel"):
            its high over the band and its low under it, and a faint line down to the step it came from. */}
        {cornerLabels.map(({ key, ex, tx, anchor, high, low, hy, ly, marker, at }) => {
          const foot = low === null ? y(at.lowCents) + 4 : ly + 4;
          return (
            <g key={key} className="num" fontSize="11" fill={C.ink2}>
              <text x={tx} y={hy} textAnchor={anchor} {...HALO}>
                {high}
              </text>
              {low === null ? null : (
                <text x={tx} y={ly} textAnchor={anchor} {...HALO}>
                  {low}
                </text>
              )}
              {marker !== null && foot < MARK_Y - 12 ? <path d={`M${ex} ${foot} V${MARK_Y - 11}`} stroke={C.ink3} strokeOpacity="0.5" strokeDasharray="2 3" /> : null}
            </g>
          );
        })}
        {/* Where the lines ahead meet, at the far end: the one figure printed ahead of today, and plainly made up (L-11),
            under the point (over it where the point sits low). */}
        {end && s.future.length > 1
          ? (() => {
              const ey = y(s.future.at(-1)!.highCents);
              const below = ey + 30 < BASE;
              return (
                <g className="num" textAnchor="end" {...HALO}>
                  <text x={R} y={below ? ey + 15 : ey - 19} fontSize="11" fill={C.ink}>
                    {end.figure}
                  </text>
                  <text x={R} y={below ? ey + 28 : ey - 7} fontSize="10" fill={C.ink3}>
                    {end.madeUp}
                  </text>
                </g>
              );
            })()
          : null}
        {/* Over the budget: the part of the estimate the cap holds down, above the top, so far and ahead (design study 17). No figure for it ahead of today. */}
        {[...overRuns(s.past, true), ...overRuns(s.future)].map((run, i) => (
          <g key={`over-${i}`}>
            <polygon points={[...run.map((p) => `${x(p.on).toFixed(1)},${y(overTop(p)).toFixed(1)}`), ...run.map((p) => `${x(p.on).toFixed(1)},${y(p.highCents).toFixed(1)}`).reverse()].join(' ')} fill={`url(#${id}-over)`} />
            <path d={`M${run.map((p) => `${x(p.on).toFixed(1)} ${y(overTop(p)).toFixed(1)}`).join(' L')}`} fill="none" stroke={C.bengara} strokeDasharray={run[0]!.projected ? '3 3' : undefined} strokeOpacity="0.8" />
          </g>
        ))}
        {s.past.length && s.past.at(-1)!.overCents && y(s.past.at(-1)!.highCents) - y(s.past.at(-1)!.highCents + s.past.at(-1)!.overCents!) > 16 ? (
          <text x={x(s.now) - 6} y={y(s.past.at(-1)!.highCents + s.past.at(-1)!.overCents!) - 6} textAnchor="end" className="num" fontSize="11" fill={C.bengara} {...HALO}>
            {k(s.past.at(-1)!.overCents!)} over the budget
          </text>
        ) : null}
        {f.ceiling ? (
          <g>
            {/* The cap from the day the budget was confirmed; under it, dotted, what is left for the work. */}
            <path d={`M${f.cap ? x(f.cap.on) : L} ${y(f.ceiling.totalCents)} H${R}`} stroke={C.ink} strokeWidth="1.5" />
            {f.cap && f.cap.workCents < f.cap.totalCents ? <path d={`M${x(f.cap.on)} ${y(f.cap.workCents)} H${R}`} stroke={C.ink3} strokeDasharray="1 3" /> : null}
            <text x={R} y={y(f.ceiling.totalCents) - 6} textAnchor="end" className="num" fontSize="11" fill={C.ink} {...HALO}>
              Budget {k(f.ceiling.totalCents)}, all in
            </text>
          </g>
        ) : null}
        {f.spent.length ? (
          <text x={x(s.now) - 6} y={Math.max(TOP + 12, y(f.spent.at(-1)!.cumulativeCents) - 6)} textAnchor="end" className="num" fontSize="11" fill={C.moss} {...HALO}>
            Billed {k(f.spent.at(-1)!.cumulativeCents)}
          </text>
        ) : null}
        {f.prototype ? null : f.endedOn ? (
          <g>
            <path d={`M${x(f.endedOn)} ${TOP - 14} V${AXIS}`} stroke={C.bengara} strokeWidth="1.4" />
            <text x={x(f.endedOn) + 6} y={TOP - 4} className="num" fontSize="10" letterSpacing="0.08em" fill={C.bengara}>
              ENDED
            </text>
          </g>
        ) : (
          <g>
            <path d={`M${x(f.today)} ${TOP - 14} V${AXIS}`} stroke={C.cedar} strokeWidth="1.4" />
            <text x={x(f.today) + 6} y={TOP - 4} className="num" fontSize="10" letterSpacing="0.08em" fill={C.cedar}>
              TODAY
            </text>
          </g>
        )}
        <path d={`M${L} ${AXIS} H${R}`} stroke={C.ruleStrong} />
        {/* The floor the price is drawn from (#129): never read as zero. */}
        {fl.floorCents > 0 ? (
          <text x={R - 2} y={BASE - 4} textAnchor="end" className="num" fontSize="10" fill={C.ink3} {...HALO}>
            {k(fl.floorCents)}
          </text>
        ) : null}
        {s.ticks.map((tk) => (
          <g key={tk.on}>
            <path d={`M${x(tk.on)} ${AXIS} V${AXIS + 5}`} stroke={C.ruleStrong} />
            <text x={x(tk.on)} y={AXIS + 18} textAnchor="middle" className="num" fontSize="10" fill={C.ink3}>
              {tk.label}
            </text>
          </g>
        ))}
        {f.gates.map((g) =>
          g.on && g.state !== 'passed' && !g.placed ? (
            <path key={`span-${g.key}`} d={`M${x(new Date(time(g.on) - g.giveOrTakeDays * DAY).toISOString().slice(0, 10))} ${AXIS} H${x(new Date(time(g.on) + g.giveOrTakeDays * DAY).toISOString().slice(0, 10))}`} stroke={C.span} strokeWidth="5" strokeLinecap="round" />
          ) : null,
        )}
        {f.gates.map((g) =>
          g.on && g.originalOn && g.slipDays > 0 ? <path key={`slip-${g.key}`} d={`M${x(g.originalOn)} ${AXIS} H${x(g.on)}`} stroke={C.bengara} strokeWidth="2.5" /> : null,
        )}
        {f.gates.map((g) => (g.on ? <Mark key={g.key} g={g} x={x(g.on)} y={AXIS} /> : null))}
        {/* The schedule: the first plan in slate, anything it has been pushed out by in bengara. */}
        <text x={L} y={287} className="num" fontSize="10" letterSpacing="0.08em" fill={C.ink3}>
          {f.prototype ? 'A JOB’S TIME, BY THE PHASES’ SHARES' : 'SCHEDULE'}
        </text>
        {sched.projectedEndOn || sched.plannedEndOn ? (
          <g>
            <path d={`M${x(firstGate)} 292 H${x(sched.plannedEndOn ?? sched.projectedEndOn!)}`} stroke={C.slate} strokeOpacity="0.75" strokeWidth="6" />
            {sched.slipDays > 0 && sched.plannedEndOn && sched.projectedEndOn ? (
              <g>
                <path d={`M${x(sched.plannedEndOn)} 292 H${x(sched.projectedEndOn)}`} stroke={C.bengara} strokeWidth="6" />
                <text x={x(sched.projectedEndOn) - 4} y={284} textAnchor="end" className="num" fontSize="10" fill={C.bengara}>
                  pushed out {weeks(sched.slipDays)}
                </text>
              </g>
            ) : null}
          </g>
        ) : null}
      </svg>
        {f.prototype
          ? null
          : set.map(({ e, i }) => (
              <EstimateMarker key={`mark-${i}`} e={e} selected={est === i} onPick={() => setEst(est === i ? null : i)} style={{ left: `${(x(e.on) / W) * 100}%`, top: `${((MARK_Y - cut) / (300 - cut)) * 100}%` }} />
            ))}
      </div>
      {est !== null && f.estimates[est] ? <EstimateDetail e={f.estimates[est]!} onClose={() => setEst(null)} /> : null}
      <div ref={strip} className="relative" style={{ height: `${labelRows * 52}px` }}>
        {placed.map(({ g, i, left, row }) => (
          <GateButton
            key={g.key}
            g={g}
            when={markWhen(f, g)}
            selected={i === sel}
            onPick={() => onPick(i)}
            style={{ left: `${left}px`, top: `${row * 52}px`, width: `${LABEL_W}px`, alignItems: 'center', textAlign: 'center' }}
          />
        ))}
      </div>
    </div>
  );
}

/** Phones: time runs down the page, so the long build is a long, narrow tail. */
function Down({ funnel: f, sel, onPick }: { funnel: ProjectFunnelDto; sel: number; onPick: (i: number) => void }) {
  const id = useId().replace(/:/g, '');
  const months = (time(f.endOn) - time(f.startOn)) / (30.4 * DAY);
  // The master's prototypical job has no months: its time, the whole of it, down a phone's screen.
  const H = f.prototype ? 420 : Math.max(420, Math.round(months * 24));
  const [TOP, W, L, R] = [16, 210, 34, 206];
  // Its axis labels one under another at 9: a line's room between two.
  const s = scales(f, { length: H - TOP - 12, quarters: 14, years: 14 });
  const [est, setEst] = useState<number | null>(null);
  const set = setEstimates(f, s.past, s.now);
  const corners = labelled(f, set, s.past);
  const end = endLabel(f);
  // The made-up end's lines (L-11), under the point where the lines meet: its figure at 11 and what it is made up from at
  // 10, the phone's own figures' size (a DM Mono letter is 0.6 of its size across).
  const endText = end && s.future.length > 1 ? [end.figure, ...endLines(end.madeUp, Math.floor((W - 8) / 6))] : [];
  const yT = (d: string) => TOP + s.t(d) * (H - TOP - 12);
  const xC = (c: number) => L + s.v(c) * (R - L);
  const edge = (pts: typeof f.band, key: 'lowCents' | 'highCents') => pts.map((p) => `${xC(p[key]).toFixed(1)},${yT(p.on).toFixed(1)}`);
  const poly = (pts: typeof f.band) => [...edge(pts, 'highCents'), ...edge(pts, 'lowCents').reverse()].join(' ');
  const line = (pts: typeof f.band, key: 'lowCents' | 'highCents') => `M${edge(pts, key).join(' L')}`;
  const spentPath = f.spent.length
    ? `M${L} ${yT(f.spent[0]!.on)} ` + f.spent.map((p, i) => `L${i ? xC(f.spent[i - 1]!.cumulativeCents) : L} ${yT(p.on)} L${xC(p.cumulativeCents)} ${yT(p.on)}`).join(' ') + ` L${xC(f.spent.at(-1)!.cumulativeCents)} ${yT(s.spentTo)} L${L} ${yT(s.spentTo)} Z`
    : null;
  // Labels down the left, nudged apart so each keeps a full touch target.
  const order = f.gates
    .map((g, i) => ({ g, i }))
    .filter(({ g }) => g.on)
    .sort((a, b) => yT(a.g.on!) - yT(b.g.on!));
  const placed: { g: FunnelGateDto; i: number; at: number; true: number }[] = [];
  for (const { g, i } of order) {
    const at = Math.max(yT(g.on!), (placed.at(-1)?.at ?? -Infinity) + 46, 22);
    placed.push({ g, i, at, true: yT(g.on!) });
  }
  // Each corner's figures beside it (just before today, under its line, clear of TODAY), a line below the one before
  // where two would touch.
  const downLabels: ((typeof corners)[number] & { ly: number })[] = [];
  for (const c of corners) {
    const own = !f.prototype && Math.abs(yT(c.on) - yT(s.now)) < 16 ? yT(s.now) + 13 : Math.max(TOP + 8, yT(c.on) - 4);
    const prev = downLabels.at(-1);
    downLabels.push({ ...c, ly: prev && own - prev.ly < 11 ? prev.ly + 11 : own });
  }
  const height = Math.max(H, (placed.at(-1)?.at ?? 0) + 26, endText.length ? H - 12 + 13 * endText.length + 10 : 0);
  const sched = f.schedule;
  const firstGate = f.gates.find((g) => g.on && !g.placed)?.on ?? f.startOn;
  return (
    <>
    <div className="grid grid-cols-[minmax(0,1fr)_210px] border-y border-rule">
      <div className="relative" style={{ height }}>
        {placed.map(({ g, i, at }) => (
          <GateButton key={g.key} g={g} when={markWhen(f, g)} selected={i === sel} onPick={() => onPick(i)} style={{ top: at - 22, left: 0, right: 4, alignItems: 'flex-start', textAlign: 'left' }} />
        ))}
      </div>
      <div className="relative">
      <svg width={W} height={height} viewBox={`0 0 ${W} ${height}`} className="block" role="img" aria-label={ariaSummary(f)}>
        <Defs id={id} vertical from={TOP} to={H - 12} breadth={W} />
        {/* The plants of the place through each year, growing from the timeline beside the funnel. */}
        <Seasons
          id={id}
          strips={f.seasons.down}
          direction="seasons_down"
          from={f.startOn}
          to={f.endOn}
          opacity={0.45}
          place={(tile) => {
            // Along its dates, the full width beside the funnel: never cut off at the side.
            const span = (s.u(tile.to) - s.u(tile.from)) * (H - TOP - 12);
            const width = SEASON_DRAWING.seasons_down.breadth;
            return { x: W - width, y: TOP + s.u(tile.from) * (H - TOP - 12) - span * SEAM, width, height: span * (1 + 2 * SEAM), w: wanted(width, 3) };
          }}
        />
        {s.ticks.map((tk) => (
          <g key={tk.on}>
            <path d={`M${L} ${yT(tk.on)} H${R}`} stroke={C.rule} strokeOpacity="0.55" />
            {f.prototype || Math.abs(yT(tk.on) - yT(s.now)) > 14 ? (
              <text x={L + 8} y={yT(tk.on) - 3} className="num" fontSize="9" fill={C.ink3} {...HALO}>
                {tk.label}
              </text>
            ) : null}
          </g>
        ))}
        {spentPath ? <path d={spentPath} fill={C.moss} fillOpacity="0.8" /> : null}
        {s.past.length > 1 ? <polygon points={poly(s.past)} fill={C.stone} fillOpacity="0.92" /> : null}
        {/* What lies ahead, lighter, to its one point at the far end (Jason, 2026-10-07). */}
        {s.future.length > 1 ? <polygon points={poly(s.future)} fill={C.stone} fillOpacity="0.38" /> : null}
        {s.past.length > 1 ? <path d={`${line(s.past, 'highCents')} ${line(s.past, 'lowCents')}`} fill="none" stroke={C.ink} /> : null}
        {s.future.length > 1 ? <path d={`${line(s.future, 'highCents')} ${line(s.future, 'lowCents')}`} fill="none" stroke={C.ink2} strokeDasharray="3 3" /> : null}
        {/* Over the budget: the part of the estimate the cap holds down, beside the top (design study 17). */}
        {[...overRuns(s.past, true), ...overRuns(s.future)].map((run, i) => (
          <g key={`over-${i}`}>
            <polygon points={[...run.map((p) => `${xC(overTop(p)).toFixed(1)},${yT(p.on).toFixed(1)}`), ...run.map((p) => `${xC(p.highCents).toFixed(1)},${yT(p.on).toFixed(1)}`).reverse()].join(' ')} fill={`url(#${id}-over)`} />
            <path d={`M${run.map((p) => `${xC(overTop(p)).toFixed(1)} ${yT(p.on).toFixed(1)}`).join(' L')}`} fill="none" stroke={C.bengara} strokeDasharray={run[0]!.projected ? '3 3' : undefined} strokeOpacity="0.8" />
          </g>
        ))}
        {f.ceiling ? <path d={`M${xC(f.ceiling.totalCents)} ${yT(f.cap ? f.cap.on : f.startOn)} V${yT(f.endOn)}`} stroke={C.ink} strokeWidth="1.5" /> : null}
        {f.cap && f.cap.workCents < f.cap.totalCents ? <path d={`M${xC(f.cap.workCents)} ${yT(f.cap.on)} V${yT(f.endOn)}`} stroke={C.ink3} strokeDasharray="1 3" /> : null}
        {downLabels.map(({ key, on, at, marker, ly }) => (
          <g key={key}>
            {marker !== null ? <path d={`M${xC(at.highCents) + 3} ${yT(on)} H${W - 22}`} stroke={C.ink3} strokeOpacity="0.5" strokeDasharray="2 3" /> : null}
            <text x={xC(at.lowCents) - 4} y={ly} textAnchor="end" className="num" fontSize="10" fill={C.ink2} {...HALO}>
              {f.prototype ? spreadWords(at) : at.lowCents === at.highCents ? k(at.lowCents) : `${k(at.lowCents)}–${k(at.highCents)}`}
            </text>
          </g>
        ))}
        {endText.length
          ? (() => {
              // Where the lines ahead meet: the one figure printed ahead of today, and plainly made up (L-11).
              const wide = Math.max(...endText.map((l, i) => l.length * (i ? 6 : 6.6)));
              const ex = Math.min(Math.max(xC(s.future.at(-1)!.highCents), wide / 2 + 4), W - wide / 2 - 4);
              return (
                <g className="num" textAnchor="middle" {...HALO}>
                  {endText.map((l, i) => (
                    <text key={i} x={ex} y={H - 12 + 15 + i * 13} fontSize={i ? 10 : 11} fill={i ? C.ink3 : C.ink}>
                      {l}
                    </text>
                  ))}
                </g>
              );
            })()
          : null}
        {!corners.length && s.past.length ? (
          <text x={xC(s.past.at(-1)!.lowCents) - 5} y={yT(s.now) + 13} textAnchor="end" className="num" fontSize="10" fill={C.ink2} {...HALO}>
            {s.past.at(-1)!.lowCents === s.past.at(-1)!.highCents ? k(s.past.at(-1)!.lowCents) : `${k(s.past.at(-1)!.lowCents)}–${k(s.past.at(-1)!.highCents)}`}
          </text>
        ) : null}
        {f.prototype ? null : (
          <>
            <path d={`M0 ${yT(s.now)} H${W}`} stroke={f.endedOn ? C.bengara : C.cedar} strokeWidth="1.4" />
            <text x={L} y={yT(s.now) - 5} className="num" fontSize="10" letterSpacing="0.08em" fill={f.endedOn ? C.bengara : C.cedar} {...HALO}>
              {f.endedOn ? 'ENDED' : 'TODAY'}
            </text>
          </>
        )}
        {/* The schedule down the left edge: the first plan in slate, any push in bengara. */}
        {sched.projectedEndOn || sched.plannedEndOn ? (
          <g>
            <path d={`M24 ${yT(firstGate)} V${yT(sched.plannedEndOn ?? sched.projectedEndOn!)}`} stroke={C.slate} strokeOpacity="0.75" strokeWidth="5" />
            {sched.slipDays > 0 && sched.plannedEndOn && sched.projectedEndOn ? (
              <path d={`M24 ${yT(sched.plannedEndOn)} V${yT(sched.projectedEndOn)}`} stroke={C.bengara} strokeWidth="5" />
            ) : null}
          </g>
        ) : null}
        {f.gates.map((g) =>
          g.on && g.state !== 'passed' && !g.placed ? (
            <path key={`span-${g.key}`} d={`M${L} ${yT(new Date(time(g.on) - g.giveOrTakeDays * DAY).toISOString().slice(0, 10))} V${yT(new Date(time(g.on) + g.giveOrTakeDays * DAY).toISOString().slice(0, 10))}`} stroke={C.span} strokeWidth="4" strokeLinecap="round" />
          ) : null,
        )}
        {f.gates.map((g) =>
          g.on && g.originalOn && g.slipDays > 0 ? <path key={`slip-${g.key}`} d={`M${L} ${yT(g.originalOn)} V${yT(g.on)}`} stroke={C.bengara} strokeWidth="2.5" /> : null,
        )}
        {placed.map(({ g, at, true: ty }) => (
          <g key={g.key}>
            <path d={`M0 ${at} L12 ${ty} H${L - 6}`} fill="none" stroke={C.ink3} strokeWidth="0.8" />
            <Mark g={g} x={L} y={ty} size={11} />
          </g>
        ))}
      </svg>
        {f.prototype
          ? null
          : set.map(({ e, i }) => (
              <EstimateMarker key={`mark-${i}`} e={e} selected={est === i} onPick={() => setEst(est === i ? null : i)} style={{ left: `${W - 12}px`, top: `${yT(e.on)}px` }} />
            ))}
      </div>
    </div>
    {est !== null && f.estimates[est] ? <EstimateDetail e={f.estimates[est]!} onClose={() => setEst(null)} /> : null}
    </>
  );
}

/** The funnel's key: only what this funnel draws (funnelKey.ts), each swatch drawn as the funnel draws it. */
function Legend({ funnel: f }: { funnel: ProjectFunnelDto }) {
  const entries = funnelKey(f);
  if (!entries.length) return null;
  const swatch = (style: React.CSSProperties) => <span aria-hidden="true" className="inline-block h-2.5 w-4" style={style} />;
  // The marks' standings drawn in the shape most of them are: a diamond while any step closes a phase.
  const shape: FunnelGateDto['mark'] = entries.includes('closes') ? 'gate' : 'ring';
  const mark = (m: Pick<FunnelGateDto, 'mark' | 'state'>) => (
    <svg width="14" height="14" aria-hidden="true">
      <Mark g={m} x={7} y={7} size={11} />
    </svg>
  );
  const look: Record<FunnelKeyEntry, ReactNode> = {
    closes: mark({ mark: 'gate', state: 'upcoming' }),
    milestone: mark({ mark: 'ring', state: 'upcoming' }),
    estimate: mark({ mark: 'ring', state: 'upcoming' }),
    set: <span aria-hidden="true" className="inline-block h-3 w-3 rounded-full border border-ink-3 bg-paper" />,
    done: mark({ mark: shape, state: 'passed' }),
    next: mark({ mark: shape, state: 'next' }),
    notYet: mark({ mark: shape, state: 'upcoming' }),
    about: <span className="num text-ink-3">~</span>,
    soFar: swatch({ background: C.stone, boxShadow: `inset 0 0 0 1px ${C.ink}` }),
    spread: swatch({ background: C.stone, boxShadow: `inset 0 0 0 1px ${C.ink}` }),
    ahead: swatch({ background: C.stone, opacity: 0.7, outline: `1px dashed ${C.ink2}`, outlineOffset: -1 }),
    billed: swatch({ background: C.moss }),
    budget: swatch({ borderTop: `1.5px solid ${C.ink}`, height: 0 }),
    work: swatch({ borderTop: `1.5px dotted ${C.ink3}`, height: 0 }),
    over: swatch({ background: `repeating-linear-gradient(45deg, transparent 0 3px, ${C.bengara} 3px 4px)`, boxShadow: `inset 0 0 0 1px ${C.bengara}`, opacity: 0.8 }),
    span: swatch({ background: C.span, borderRadius: 3, height: 5 }),
    planned: swatch({ background: C.slate, opacity: 0.75, height: 5 }),
    pushed: swatch({ background: C.bengara, height: 5 }),
  };
  return (
    <p className="mt-3 flex flex-wrap gap-x-5 gap-y-1.5 text-[0.75rem] text-ink-2">
      {entries.map((e) => (
        <span key={e} className="inline-flex items-center gap-2">
          {look[e]}
          {keyWords(e, f)}
          {e === 'budget' && f.ceiling ? <span className="num">{k(f.ceiling.totalCents)}</span> : null}
        </span>
      ))}
    </p>
  );
}

/**
 * A phase's facts, opened from the question mark beside its name (#129; Jason, 2026-10-29: "all of this info should be
 * hidden as help icons next to the funnel time line zones"): what it is, when, and how sure the price is by its end
 * (`phaseHelp`), in the box a question mark opens elsewhere.
 */
function PhaseHelpBox({ id, phase: p, funnel: f, style }: { id: string; phase: FunnelPhaseDto; funnel: ProjectFunnelDto; style?: React.CSSProperties }) {
  const h = phaseHelp(p, { ended: f.endedOn !== null, priced: f.band.length > 0 });
  return (
    <div id={id} className="mb-3 mt-1 max-w-[30rem] rounded-[2px] border border-rule-strong bg-sheet px-4 py-3 text-[0.9375rem]" style={style}>
      <p className="font-display text-[1.125rem]">{h.title}</p>
      <p className="mt-1">{h.what}</p>
      <p className="mt-1 text-ink-2">{h.when}.</p>
      <p className="mt-1 text-ink-2">{h.sure}</p>
    </div>
  );
}

/** On a phone, where the funnel runs down the page with no band over it: the phases' names in a row, each with its facts. */
function PhaseRow({ funnel: f }: { funnel: ProjectFunnelDto }) {
  const [open, setOpen] = useState<string | null>(null);
  const id = useId();
  const opened = f.phases.find((p) => p.key === open);
  return (
    <div className="mb-3">
      <ul className="flex flex-wrap gap-x-5 gap-y-2">
        {f.phases.map((p) => (
          <li key={p.key} className="flex items-center gap-1.5">
            <span className={`font-mono text-[0.8125rem] uppercase tracking-[0.06em] ${p.state === 'now' ? 'text-ink' : 'text-ink-3'}`}>{p.name}</span>
            <HowMark small open={open === p.key} onToggle={() => setOpen(open === p.key ? null : p.key)} controls={id} label={`What ${p.name} is`} />
          </li>
        ))}
      </ul>
      {opened ? <PhaseHelpBox id={id} phase={opened} funnel={f} /> : null}
    </div>
  );
}

export function GateDetail({ gate: g, staff, ended = false, share = null, onClose }: { gate: FunnelGateDto; staff: boolean; ended?: boolean; share?: string | null; onClose?: () => void }) {
  const ring = g.mark === 'ring';
  // On the master's prototypical job (L-12): where its share of the time places it.
  const when = share
    ? `At ${share} of a job’s time, placed by the phases’ shares`
    : g.state === 'passed' && g.on
      ? `${ring ? 'Reached' : 'Done'} ${fullDay(g.on)}`
      : g.placed
        ? `${g.state === 'next' ? 'Next · ' : ''}${PLACED}`
        : g.on
        ? `${g.state === 'next' ? 'Next · ' : ''}about ${monthYear(g.on)}, give or take ${weeks(g.giveOrTakeDays)}`
        : ended
          ? 'Not reached'
          : `${g.state === 'next' ? 'Next · ' : ''}not dated yet`;
  return (
    <section className="mt-5 border-y border-rule py-4">
      <p className="flex flex-wrap items-baseline gap-x-3">
        <span className="font-display text-[1.375rem]">{g.name}</span>
        <span className="label">{when}</span>
        {onClose ? (
          <button type="button" onClick={onClose} className="link hit ml-auto text-[0.875rem] text-ink-2">
            Close
          </button>
        ) : null}
      </p>
      {g.note ? <p className="mt-2 max-w-2xl text-ink-2">{g.note}</p> : null}
      {/* The estimate it delivers (design study 17): the one set on it, or that one comes here. A figure only for the past. */}
      {g.deliversEstimate ? (
        <p className="mt-2 text-[0.875rem]">
          {g.estimate ? (
            <>
              Estimate: <span className="num">{g.estimate.lowCents === g.estimate.highCents ? dollars(g.estimate.lowCents) : `${dollars(g.estimate.lowCents)} to ${dollars(g.estimate.highCents)}`}</span>, set {fullDay(g.estimate.on)}.
              {g.estimate.note ? ` ${g.estimate.note}` : ''}
            </>
          ) : g.state === 'passed' ? (
            'It delivers an estimate; none was set.'
          ) : (
            'A new estimate comes here.'
          )}
        </p>
      ) : null}
      {share && g.range ? <p className="num mt-2 text-[0.8125rem]">How sure the price is once it is done: {spreadWords(g.range)}</p> : null}
      {g.state === 'passed' && g.range ? (
        <p className="num mt-2 text-[0.8125rem]">
          The price then: {g.range.lowCents === g.range.highCents ? dollars(g.range.lowCents) : `${dollars(g.range.lowCents)} – ${dollars(g.range.highCents)}`}
        </p>
      ) : null}
      {g.slipDays > 0 && g.originalOn ? (
        <p className="mt-2 text-[0.875rem] text-alert">
          {g.state === 'passed' ? (ring ? 'Reached' : 'Done') : 'Moved'} {weeks(g.slipDays)} later than first planned ({fullDay(g.originalOn)})
          {g.slipCause === 'change_order' ? ', by a change order' : ''}
          {staff && g.slipReason ? `: ${g.slipReason}` : '.'}
        </p>
      ) : null}
    </section>
  );
}

function ariaSummary(f: ProjectFunnelDto): string {
  if (f.prototype) return f.band.length ? `How sure the price is: ${spreadWords(f.band[0]!)} at the start, ${spreadWords(f.band.at(-1)!)} by the end.` : 'No phase has a share of the time yet.';
  const now = f.band.filter((p) => !p.projected).at(-1);
  const final = f.gates.at(-1);
  const parts = [now ? `The price now: ${k(now.lowCents)} to ${k(now.highCents)}.` : 'No price yet.'];
  const end = endInWords(f);
  if (end) parts.push(`Ahead, the lines meet at ${end}.`);
  if (f.spent.length) parts.push(`Billed ${k(f.spent.at(-1)!.cumulativeCents)}.`);
  if (f.endedOn) parts.push(`Ended ${fullDay(f.endedOn)}.`);
  else if (final?.on && !final.placed) parts.push(`Final expected about ${monthYear(final.on)}.`);
  if (f.schedule.slipDays > 0) parts.push(`Pushed out ${weeks(f.schedule.slipDays)} from the first plan.`);
  return parts.join(' ');
}
